import { Link } from "@tanstack/react-router";
import markAsset from "@/assets/forexmind-mark.webp.asset.json";

type LogoSize = "sm" | "md" | "lg" | "xl";

const sizeMap: Record<LogoSize, { mark: string; text: string; tagline: string; gap: string }> = {
  sm: { mark: "h-7 w-7", text: "text-sm", tagline: "text-[9px]", gap: "gap-2" },
  md: { mark: "h-9 w-9", text: "text-base", tagline: "text-[10px]", gap: "gap-2.5" },
  lg: { mark: "h-14 w-14", text: "text-2xl", tagline: "text-[11px]", gap: "gap-3" },
  xl: { mark: "h-20 w-20", text: "text-4xl", tagline: "text-xs", gap: "gap-4" },
};

/**
 * FM monogram mark — official ForexMind AI brand mark.
 * Served as 96×96 WebP (~1.4KB) from CDN.
 */
export function LogoMark({ className = "" }: { className?: string }) {
  return (
    <img
      src={markAsset.url}
      alt="ForexMind AI"
      width={48}
      height={48}
      loading="eager"
      fetchPriority="high"
      decoding="async"
      className={`object-contain ${className}`}
      draggable={false}
    />
  );
}

interface LogoProps {
  size?: LogoSize;
  variant?: "lockup" | "stacked" | "mark";
  /** If true, renders as a div instead of a Link (for hero / auth pages where the page itself is the destination). */
  asStatic?: boolean;
  className?: string;
  showTagline?: boolean;
}

export function Logo({
  size = "md",
  variant = "lockup",
  asStatic = false,
  className = "",
  showTagline,
}: LogoProps) {
  const s = sizeMap[size];
  const tagline = showTagline ?? (size === "lg" || size === "xl");

  const wordmark = (
    <div className="flex flex-col leading-none">
      <div className={`brand-text whitespace-nowrap font-semibold tracking-tight text-foreground ${s.text}`}>
        ForexMind<span className="text-primary"> AI</span>
      </div>
      {tagline && (
        <div className={`mt-1 whitespace-nowrap uppercase tracking-[0.25em] text-muted-foreground ${s.tagline}`}>
          AI Market Intelligence
        </div>
      )}
    </div>
  );

  if (variant === "mark") {
    const inner = <LogoMark className={`${s.mark} ${className}`} />;
    if (asStatic) return inner;
    return <Link to="/" aria-label="ForexMind AI home">{inner}</Link>;
  }

  if (variant === "stacked") {
    const inner = (
      <div className={`flex flex-col items-center ${s.gap} ${className}`}>
        <LogoMark className={s.mark} />
        {wordmark}
      </div>
    );
    if (asStatic) return inner;
    return <Link to="/" aria-label="ForexMind AI home">{inner}</Link>;
  }

  const inner = (
    <div className={`flex items-center ${s.gap} ${className}`}>
      <LogoMark className={s.mark} />
      {wordmark}
    </div>
  );
  if (asStatic) return inner;
  return (
    <Link to="/" className="inline-flex items-center" aria-label="ForexMind AI home">
      {inner}
    </Link>
  );
}
